// src/utils/paymentUtils.js
import { createSetupIntent, scheduleDonations, createPaymentIntent } from '../api/payments';
import { getIpAddress } from '../api/verification';

/**
 * Validates payment form values
 * @param {Object} formValues - Form values containing customer information
 * @param {Function} t - Translation function
 * @returns {Object} - Object with isValid flag and errors object
 */
export const validatePaymentForm = (formValues, t) => {
  const errors = {};
  let isValid = true;

  // Fields required
  const requiredFields = [
    "firstName",
    "lastName",
    "email",
    "phone",
    "addressLine1",
    "postalCode",
    "selectedRegion",
    "selectedCountry"
  ];

  requiredFields.forEach((field) => {
    if (!formValues[field]) {
      isValid = false;
      errors[field] = t ? t("paymentDetailsFieldIsRequired", {
        fieldName: mapFieldToLabel(field, t)
      }) : "paymentDetailsFieldIsRequired";
    }
  });

  if (
    formValues.email &&
    !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formValues.email)
  ) {
    isValid = false;
    errors.email = t ? t("paymentDetailsInvalidEmail") : "Invalid email";
  }

  if (formValues.unitNumber && isNaN(formValues.unitNumber)) {
    isValid = false;
    errors.unitNumber = t ? t("paymentDetailsUnitMustBeNumber") : "paymentDetailsUnitMustBeNumber";
  }

  return { isValid, errors };
};

/**
 * Maps form field names to user-friendly labels
 */
export const mapFieldToLabel = (field, t) => {
  switch (field) {
    case "firstName":
      return t("paymentDetailsFirstName");
    case "middleName":
      return t("paymentDetailsMiddleName");
    case "lastName":
      return t("paymentDetailsLastName");
    case "email":
      return t("paymentDetailsEmail");
    case "phone":
      return t("paymentDetailsPhone");
    case "addressLine1":
      return t("paymentDetailsAddressLine1");
    case "addressLine2":
      return t("paymentDetailsAddressLine2");
    case "unitNumber":
      return t("paymentDetailsUnitNumber");
    case "postalCode":
      return t("paymentDetailsPostalCode");
    case "selectedRegion":
      return t("paymentDetailsSelectRegion");
    case "selectedCountry":
      return t("paymentDetailsSelectCountry");
    default:
      return field;
  }
};

/**
 * Prepares donor details object from form values
 */
export const prepareDonorDetails = (formValues) => {
  return {
    firstName: formValues.firstName,
    lastName: formValues.lastName,
    email: formValues.email,
    phone: formValues.phone,
    addressLine1: formValues.addressLine1,
    addressLine2: formValues.addressLine2,
    unit: formValues.unitNumber,
    postalCode: formValues.postalCode,
    region: formValues.selectedRegion,
    country: formValues.selectedCountry
  };
};

/**
 * Handles the card payment setup intent process
 */
export const handleCardSetup = async (stripe, elements, clientSecret, formValues, t, CardElement) => {
  const cardElement = elements.getElement(CardElement);

  return await stripe.confirmCardSetup(
    clientSecret,
    {
      payment_method: {
        card: cardElement,
        billing_details: {
          name: t("paymentDetailsNameFormat", {
            firstName: formValues.firstName,
            lastName: formValues.lastName
          }),
          email: formValues.email
        }
      }
    }
  );
};

/**
 * Handles the ACSS (online banking) setup intent process
 */
export const handleAcssSetup = async (stripe, clientSecret, formValues) => {
  const ipAddress = await getIpAddress();

  return await stripe.confirmAcssDebitSetup(
    clientSecret,
    {
      payment_method: {
        billing_details: {
          name: `${formValues.firstName} ${formValues.lastName}`,
          email: formValues.email
        }
      },
      mandate_data: {
        customer_acceptance: {
          type: "online",
          online: {
            ip_address: ipAddress,
            user_agent: navigator.userAgent
          }
        }
      }
    }
  );
};

/**
 * Process one time donation with tip
 * @param {Object} params - Parameters for creating payment intent
 * @param {number} params.tipAmount - Amount to charge as tip
 * @param {string} params.customerId - Stripe customer ID
 * @param {string} params.paymentMethodType - Type of payment method (card, acss_debit)
 * @param {string} params.ref - Optional reference code
 * @returns {Promise<Object>} - Client secret and payment intent ID
 */
export const processOneTimeDonation = async ({ amount, tipAmount, customerId, paymentMethodType, paymentMethodId, ref }) => {
  if (!amount || amount <= 0) {
    return { status: 'skipped' };
  }

  try {
    const payload = {
      amount,
      tipAmount,
      currency: 'gbp',
      customerId,
      paymentMethodType,
      paymentMethodId
    };

    if (ref) {
      payload.ref = ref;
    }

    const oneTimePaymentResponse = await createPaymentIntent(payload);

    if (oneTimePaymentResponse && oneTimePaymentResponse.clientSecret) {
      return {
        clientSecret: oneTimePaymentResponse.clientSecret,
        paymentIntentId: oneTimePaymentResponse.paymentIntentId,
        status: 'success'
      };
    }

    return { status: 'error', message: 'No client secret returned' };
  } catch (error) {
    console.error('Error processing tip payment:', error.message);
    return { status: 'error', message: error.message };
  }
};

/**
 * Confirm tip payment with Stripe
 * @param {Object} stripe - Stripe instance
 * @param {string} clientSecret - Client secret for payment confirmation
 * @param {string} paymentMethodId - Payment method ID
 * @returns {Promise<Object>} - Result of the confirmation
 */
export const confirmTipPayment = async (stripe, clientSecret, paymentMethodId) => {
  if (!clientSecret || !paymentMethodId) {
    console.log('Tip error: missing client secret or payment method ID');
    return { status: 'error', message: 'Missing client secret or payment method ID' };
  }

  try {
    const result = await stripe.confirmCardPayment(clientSecret, {
      payment_method: paymentMethodId
    });

    return { status: 'success', result };
  } catch (error) {
    console.log('Error processing tip:', error);
    return { status: 'error', message: error.message };
  }
};

/**
 * Schedule donations
 * @param {Object} params - Parameters for scheduling donations
 * @param {Array} params.donationScheduleBreakdown - Array of donation schedules
 * @param {string} params.paymentMethodId - Payment method ID
 * @param {Object} params.donorDetails - Donor details
 * @param {string} params.paymentMethodType - Type of payment method
 * @param {string} params.template - Template type
 * @param {number} params.tipAmount - Tip amount (usually 0 here to avoid double charging)
 * @param {string} params.timezone - User's timezone
 * @param {string} params.ref - Optional reference code
 * @returns {Promise<Object>} - Schedule ID and result
 */
export const processScheduledDonations = async ({
  donationScheduleBreakdown,
  paymentMethodId,
  donorDetails,
  paymentMethodType,
  template,
  tipAmount = 0,
  timezone,
  ref,
  customerId
}) => {
  try {
    const ipAddress = await getIpAddress();
    const userAgent = navigator.userAgent;

    const payload = {
      donationScheduleBreakdown,
      paymentMethodId,
      paymentMethodType,
      ipAddress,
      userAgent,
      template,
      tipAmount, // Usually 0 here to avoid double charging
      autoAjrRecurringSupport: tipAmount, // This is used to track if the user is supporting auto AJR
      timezone,
      ref,
      customerId
    };

    const response = await scheduleDonations(payload);
    return { status: 'success', scheduleId: response.scheduleId, ...response };
  } catch (error) {
    console.error('Error scheduling donation:', error.message);
    return { status: 'error', message: error.message };
  }
};

/**
 * Handle the complete payment confirmation flow with donation scheduling and tip
 * @param {Object} params - All parameters needed for confirmation
 * @returns {Promise<Object>} - Result of the confirmation process
 */
export const handleConfirmPayment = async ({
  stripe,
  tipAmount,
  customerId,
  paymentMethodId,
  paymentMethodType,
  donationScheduleBreakdown,
  donorDetails,
  template,
  timezone,
  ref,
  setLoading,
  t
}) => {
  if (setLoading) setLoading(true);

  try {
    // if template == OneTime charge donation now else process scheduled donations
      const schedulingResult = await processScheduledDonations({
        donationScheduleBreakdown,
        paymentMethodId,
        donorDetails,
        paymentMethodType,
        template,
        tipAmount, // Set to 0 to prevent double-charging
        timezone,
        ref,
        customerId
      });

      if (schedulingResult.status === 'error') {
        if (setLoading) setLoading(false);
        return {
          status: 'error',
          message: t ? t('confirmDonationError') : 'Error scheduling donations',
          error: schedulingResult.message
        };
      }

      if (setLoading) setLoading(false);
      return {
        status: 'success',
        scheduleId: schedulingResult.scheduleId,
      };
  } catch (error) {
    console.error('Error in handleConfirmPayment:', error);
    if (setLoading) setLoading(false);
    return { status: 'error', message: error.message };
  }
};

/**
 * Main function to handle the payment process and navigation flow
 */
export const handlePaymentProcess = async ({
  formValues,
  paymentMethodType,
  stripe,
  elements,
  setLoading,
  tipAmount,
  finalTipParam,
  t,
  CardElement,
  template
}) => {
  const donorDetails = prepareDonorDetails(formValues);

  try {
    setLoading(true);

    const { clientSecret, customerId } = await createSetupIntent({
      donorDetails,
      paymentMethodType,
      intervalDescription: template === "Fridays" ? "Friday donation schedule" : "One-time donation"
    });

    let setupIntent, error;

    // Handle different payment methods
    if (paymentMethodType === "acss_debit") {
      // ACSS Debit (online banking)
      const result = await handleAcssSetup(stripe, clientSecret, formValues);
      setupIntent = result.setupIntent;
      error = result.error;

      if (error) {
        console.error("Error confirming ACSS setup:", error.message);
        alert(t("paymentDetailsSaveOnlineBankingError"));
        setLoading(false);
        return false;
      }
    } else {
      // Credit Card
      const result = await handleCardSetup(stripe, elements, clientSecret, formValues, t, CardElement);
      setupIntent = result.setupIntent;
      error = result.error;

      if (error) {
        console.error("Error confirming card setup:", error.message);
        alert(t("paymentDetailsSaveCardError"));
        setLoading(false);
        return false;
      }
    }

    // Update URL parameters and move to next step
    const nextStepValues = {
      paymentMethodId: setupIntent.payment_method,
      customerId: customerId,
    };

    setLoading(false);
    return nextStepValues;
  } catch (error) {
    console.error("Error creating SetupIntent:", error.message);
    setLoading(false);
    alert(t("paymentDetailsGenericError"));
    return false;
  }
};